import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  Modal,
  Form,
  Select,
  Switch,
  InputNumber,
  Divider,
  Typography,
  Row,
  Col,
  Input,
  Button
} from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";

import { showErrorNotification, showSuccessNotification } from "../../../../utils/notification";

const { Text } = Typography;
const { Item, useForm } = Form;
const { Option } = Select;

const CourseContestLinkModal = ({
  isOpen,
  onCancel,
  onSuccess,
  getApiUrl,
  courseUniquePublicId,
  availableContests = [],
  linkedContests = [],
  activeLinkData = null,
  courseModules = []
}) => {
  const [form] = useForm();
  const [loading, setLoading] = useState(false);

  const isEdit = !!activeLinkData;

  const selectableContests = useMemo(() => {
    if (isEdit) {
      return [];
    }
    const linkedIds = new Set(
      linkedContests.map((contestDetails) => contestDetails.uniquePublicId)
    );
    return availableContests.filter(
      (contestDetails) => !linkedIds.has(contestDetails.uniquePublicId)
    );
  }, [availableContests, linkedContests, isEdit]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (isEdit) {
      const {
        displayOrder,
        preRequisitesText,
        prerequisites = [],
        settings: { isVisible } = {}
      } = activeLinkData;

      form.setFieldsValue({
        displayOrder,
        preRequisitesText,
        prerequisites,
        isVisible: isVisible ?? true
      });
    } else {
      form.resetFields();
    }
  }, [isOpen, isEdit, activeLinkData, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const payload = {
        courseUniquePublicId,
        contestUniquePublicId: isEdit
          ? activeLinkData.uniquePublicId
          : values.contestUniquePublicId,
        displayOrder: values.displayOrder,
        preRequisitesText: values.preRequisitesText,
        prerequisites: values.prerequisites || [],
        settings: {
          isVisible: values.isVisible
        }
      };

      await axios.post(getApiUrl("/link-contest"), payload);
      showSuccessNotification(isEdit ? "Contest link updated" : "Contest linked");
      onSuccess();
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={640}
      title={isEdit ? "Edit Contest Link" : "Link Contest to Course"}
    >
      <Form layout="vertical" form={form} initialValues={{ isVisible: true }}>
        {!isEdit && (
          <>
            <Text strong>Contest</Text>
            <Item
              name="contestUniquePublicId"
              rules={[{ required: true }]}
              style={{ marginTop: 8 }}
            >
              <Select
                size="large"
                placeholder="Choose contest to link"
                showSearch
                optionFilterProp="children"
              >
                {selectableContests.map((contestDetails) => (
                  <Option key={contestDetails.uniquePublicId} value={contestDetails.uniquePublicId}>
                    {contestDetails.name}
                  </Option>
                ))}
              </Select>
            </Item>
            <Divider />
          </>
        )}

        <Text strong>Ordering</Text>
        <Row gutter={16} style={{ marginTop: 8 }}>
          <Col xs={24} sm={12}>
            <Item name="displayOrder" label="Display Order" rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: "100%" }} placeholder="Ordering position" />
            </Item>
          </Col>
        </Row>

        <Divider />

        <Text strong>Access & Prerequisites</Text>
        <Item
          name="preRequisitesText"
          label="Requirement Display Text"
          style={{ marginTop: 8 }}
          tooltip="This message will be shown to students if the contest is locked"
        >
          <Input placeholder="e.g. Complete Module 1 to unlock" />
        </Item>

        <Item label="Required Modules">
          <Form.List name="prerequisites">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={12} align="middle" style={{ marginBottom: 8 }}>
                    <Col flex="auto">
                      <Item
                        {...restField}
                        name={[name, "moduleUniquePublicId"]}
                        rules={[{ required: true, message: "Select module" }]}
                        noStyle
                      >
                        <Select placeholder="Select Module" showSearch style={{ width: "100%" }}>
                          {courseModules.map((m) => (
                            <Option key={m.uniquePublicId} value={m.uniquePublicId}>
                              {m.title}
                            </Option>
                          ))}
                        </Select>
                      </Item>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Item
                        {...restField}
                        name={[name, "isRequired"]}
                        valuePropName="checked"
                        initialValue={true}
                        noStyle
                      >
                        <Switch checkedChildren="Required" unCheckedChildren="Optional" />
                      </Item>
                    </Col>
                    <Col xs={4} sm={1}>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                  style={{ marginTop: fields.length > 0 ? 8 : 0 }}
                >
                  Add Prerequisite Module
                </Button>
              </>
            )}
          </Form.List>
        </Item>

        <Divider />

        <Text strong>Visibility</Text>
        <div style={{ marginTop: 12 }}>
          <Item
            name="isVisible"
            valuePropName="checked"
            label="Contest Visibility"
            help="Determine if students can see this contest in the list"
          >
            <Switch unCheckedChildren="Hidden" checkedChildren="Visible" />
          </Item>
        </div>
      </Form>
    </Modal>
  );
};

export default CourseContestLinkModal;
